/**
 * Storage utilities for handling localStorage quota issues
 */

/**
 * Safely set item in localStorage with quota handling
 * @param {string} key - The key to store under
 * @param {any} value - The value to store (will be JSON.stringify'ed)
 * @param {boolean} compress - Whether to compress the data (for large objects)
 * @returns {boolean} - Success status
 */
export const safeLocalStorageSetItem = (key, value, compress = false) => {
  try {
    let dataToStore = value

    // For companies data, store only essential fields to reduce size
    if (key === 'companies' && Array.isArray(value)) {
      dataToStore = value.map(company => ({
        id: company.id,
        name: company.name,
        // Add other essential fields if needed
        ...(company.logo && { logo: company.logo }),
      }))
    }

    localStorage.setItem(key, JSON.stringify(dataToStore))
    return true
  } catch (error) {
    if (error.name === 'QuotaExceededError') {
      console.warn(`localStorage quota exceeded when storing '${key}'. Attempting cleanup...`)

      // Try to clear non-essential data and retry
      const nonEssentialKeys = ['companies', 'modules', 'permissions']
      const currentKey = key

      for (const keyToRemove of nonEssentialKeys) {
        if (keyToRemove !== currentKey) {
          localStorage.removeItem(keyToRemove)
        }
      }

      // Retry once after cleanup
      try {
        let dataToStore = value
        if (key === 'companies' && Array.isArray(value)) {
          dataToStore = value.map(company => ({
            id: company.id,
            name: company.name,
          }))
        }
        localStorage.setItem(key, JSON.stringify(dataToStore))
        return true
      } catch (retryError) {
        console.error(`Failed to store '${key}' even after cleanup:`, retryError)
        return false
      }
    } else {
      console.error(`Error storing '${key}' in localStorage:`, error)
      return false
    }
  }
}

/**
 * Safely get item from localStorage with fallback
 * @param {string} key - The key to retrieve
 * @param {any} fallback - Fallback value if key doesn't exist or parsing fails
 * @returns {any} - The parsed value or fallback
 */
export const safeLocalStorageGetItem = (key, fallback = null) => {
  try {
    const item = localStorage.getItem(key)
    return item ? JSON.parse(item) : fallback
  } catch (error) {
    console.error(`Error parsing '${key}' from localStorage:`, error)
    return fallback
  }
}

/**
 * Check if localStorage is available and has space
 * @returns {boolean}
 */
export const isLocalStorageAvailable = () => {
  try {
    const test = '__localStorage_test__'
    localStorage.setItem(test, 'test')
    localStorage.removeItem(test)
    return true
  } catch (error) {
    return false
  }
}

/**
 * Get localStorage usage info
 * @returns {object} Usage statistics
 */
export const getLocalStorageInfo = () => {
  if (!isLocalStorageAvailable()) {
    return { available: false }
  }

  let totalSize = 0
  const items = {}

  for (const key in localStorage) {
    if (localStorage.hasOwnProperty(key)) {
      const size = localStorage[key].length
      totalSize += size
      items[key] = size
    }
  }

  return {
    available: true,
    totalSize,
    items,
    // Rough estimate - actual limits vary by browser
    estimatedLimit: 5 * 1024 * 1024, // 5MB
    percentUsed: Math.round((totalSize / (5 * 1024 * 1024)) * 100),
  }
}
